import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { db, deleteUser } from "../../config/firebase";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  CircularProgress,
  Box,
  TextField,
  Divider,
  Snackbar,
  Alert,
  Button,
  Container,
  Chip,
  Checkbox,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faXmark, faLock } from "@fortawesome/free-solid-svg-icons";
import EditAvatar from "../CcpAdmin/CcpAdminUsers/EditForm/Avatar";
import TpAddUserModal from "./TpAddUserModal";
import EditTpUser from "./EditTpUser";
import TpUserTypes from "./TpUserTypes";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog";

const TpUsersByType = ({
  userType,
  title,
  icon,
  iconColor,
  extraHeaderActions,
  enableBulkDelete = false,
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [userTypesModalOpen, setUserTypesModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);

  // The "admin" and "inactive" userType values already have their own
  // bespoke meaning in fetchUsersByType below, so the Active/Inactive tab
  // split only applies to a plain type (staff/coordinator/pathwayManager/
  // instructor) — an "Inactive Users" page with its own Active/Inactive
  // sub-tabs wouldn't make sense.
  const isTabbedType = userType !== "admin" && userType !== "inactive";

  const fetchUsersByType = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const filteredUsers = usersSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((user) => {
          // Filter by specific userType
          if (userType === "admin") {
            // Only show users who have admin flag in userTypes OR legacy techPrepAdminFlag
            // But exclude inactive users (those with legacy flag but no active userTypes)
            const hasAdminType = user.userTypes?.admin === true;
            const hasLegacyFlag =
              user.techPrepAdminFlag === true &&
              user.userTypes?.admin !== false;

            // Check if user would be considered inactive
            const allUserTypesFalse =
              user.userTypes &&
              Object.values(user.userTypes).every(
                (value) => value === false || value === 0
              );
            const noUserTypes =
              !user.userTypes || Object.keys(user.userTypes).length === 0;
            const isInactive =
              user.techPrepAdminFlag === true &&
              (allUserTypesFalse || noUserTypes);

            return (hasAdminType || hasLegacyFlag) && !isInactive;
          } else if (userType === "inactive") {
            // A TP portal user (identified by having a userTypes object, or
            // the legacy techPrepAdminFlag) with every type unchecked has
            // nowhere else to show up. Relying on techPrepAdminFlag alone
            // only caught former Admins — a Staff/Coordinator/Pathway
            // Manager user with no other type checked would vanish from
            // every tab (Staff, Coordinators, etc. all require their own
            // type to be true, and this "inactive" filter used to require
            // techPrepAdminFlag === true too) even though their Firestore
            // doc was never deleted. Catch any TP user here instead.
            const isTpUser =
              user.userTypes !== undefined || user.techPrepAdminFlag !== undefined;
            const allUserTypesFalse =
              user.userTypes &&
              Object.values(user.userTypes).every(
                (value) => value === false || value === 0
              );
            const noUserTypes =
              !user.userTypes || Object.keys(user.userTypes).length === 0;
            return isTpUser && (allUserTypesFalse || noUserTypes);
          } else {
            return user.userTypes?.[userType] === true;
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setUsers(filteredUsers);
    } catch (error) {
      console.error(`Error fetching ${userType} users: `, error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchUsersByType();
  }, [userType]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedForDelete([]);
  };

  // Same convention as EditTpInstructor/TpInstructors: a dedicated Active
  // field (-1 active / 0 inactive), missing on most pre-existing docs so
  // treated as active by default.
  const isUserInactive = (user) => {
    const activeVal = user.Active !== undefined ? user.Active : user.active;
    return activeVal !== undefined && String(activeVal) === "0";
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
    // Force a refresh to ensure any user type changes are reflected
    fetchUsersByType();
  };

  const handleEditSuccess = () => {
    // Add a small delay to ensure Firestore changes are propagated
    setTimeout(() => {
      fetchUsersByType();
    }, 500);
    setSnackbarMessage("User has been updated");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAvatarUploadSuccess = () => {
    fetchUsersByType();
  };

  const handlePasswordReset = async (user) => {
    const email = user.email;
    if (!email) {
      setSnackbarMessage("This user has no email on file.");
      setSnackbarOpen(true);
      return;
    }
    setResettingPasswordFor(user.id);
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setSnackbarMessage(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setSnackbarMessage(
        error.message || "Error sending password reset email",
      );
    } finally {
      setResettingPasswordFor(null);
      setSnackbarOpen(true);
    }
  };

  // users already contains every member of this type regardless of Active
  // state (fetchUsersByType's filter doesn't look at Active) — split into
  // Active/Inactive tab lists here, then apply the search term on top.
  const activeTypeUsers = isTabbedType
    ? users.filter((user) => !isUserInactive(user))
    : users;
  const inactiveTypeUsers = isTabbedType
    ? users.filter((user) => isUserInactive(user))
    : [];
  const usersForCurrentTab =
    isTabbedType && tabValue === 1 ? inactiveTypeUsers : activeTypeUsers;

  const filteredUsers = usersForCurrentTab.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectUser = (id) => {
    setSelectedForDelete((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAllUsers = () => {
    setSelectedForDelete((prev) =>
      prev.length === filteredUsers.length
        ? []
        : filteredUsers.map((u) => u.id),
    );
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (selectedForDelete.length === 0) return;
    setBulkDeleteConfirmOpen(true);
  };

  // Temporary bulk-delete tool: removes selected users from both Firebase
  // Auth and the users collection via the deleteUser callable (unlike the
  // single-user delete in EditTpUser, which only removes the Firestore doc
  // and leaves the Auth account behind).
  const handleBulkDeleteConfirmed = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of selectedForDelete) {
      const result = await deleteUser(id);
      if (result.success) {
        successCount += 1;
      } else {
        failCount += 1;
      }
    }

    setBulkDeleting(false);
    setBulkDeleteConfirmOpen(false);
    setSelectedForDelete([]);
    fetchUsersByType();
    setSnackbarMessage(
      failCount > 0
        ? `Deleted ${successCount} user${successCount === 1 ? "" : "s"}, ${failCount} failed to delete`
        : `Deleted ${successCount} user${successCount === 1 ? "" : "s"}`,
    );
    setSnackbarOpen(true);
  };

  const getUserTypeLabels = (user) => {
    const types = [];

    // Check if user is inactive: a TP portal user (userTypes object, or the
    // legacy techPrepAdminFlag) with no active userTypes. Matches the
    // "inactive" filter above so a chip rendered on the Inactive tab and the
    // filter that put the user there always agree.
    const isTpUser =
      user.userTypes !== undefined || user.techPrepAdminFlag !== undefined;
    const allUserTypesFalse =
      user.userTypes &&
      Object.values(user.userTypes).every(
        (value) => value === false || value === 0
      );
    const noUserTypes =
      !user.userTypes || Object.keys(user.userTypes).length === 0;
    // Either the cross-type "every type unchecked" signal, or this type's
    // own explicit Active toggle, counts as inactive.
    const isInactive =
      (isTpUser && (allUserTypesFalse || noUserTypes)) ||
      isUserInactive(user);

    if (isInactive) {
      types.push("Inactive");
    } else {
      // For admin, prioritize userTypes.admin, but fall back to legacy flag if userTypes.admin is undefined
      const isAdmin =
        user.userTypes?.admin === true ||
        (user.userTypes?.admin !== false && user.techPrepAdminFlag === true);
      if (isAdmin) types.push("Admin");
      if (user.userTypes?.coordinator) types.push("Coordinator");
      if (user.userTypes?.instructor) types.push("Instructor");
      if (user.userTypes?.staff) types.push("Staff");
      if (user.userTypes?.pathwayManager) types.push("Pathway Manager");
    }
    return types;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 15 }}>
      <Box display="flex" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
          {icon && (
            <FontAwesomeIcon
              icon={icon}
              style={{
                color: iconColor,
                fontSize: "2rem",
                marginRight: "16px",
              }}
            />
          )}
          <Typography variant="h4" component="div" gutterBottom>
            {title}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {typeof extraHeaderActions === "function"
            ? extraHeaderActions(fetchUsersByType)
            : extraHeaderActions}
          <TpAddUserModal onSuccess={fetchUsersByType} />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setUserTypesModalOpen(true)}
          >
            Usertypes
          </Button>
        </Box>
        <TpUserTypes
          open={userTypesModalOpen}
          onClose={() => setUserTypesModalOpen(false)}
        />
      </Box>

      {isTabbedType && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Active (${activeTypeUsers.length})`} />
            <Tab label={`Inactive (${inactiveTypeUsers.length})`} />
          </Tabs>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h6" color="text.secondary">
          {filteredUsers.length}{" "}
          {isTabbedType && tabValue === 1 ? "inactive " : ""}
          {userType === "admin"
            ? "admin"
            : userType === "inactive"
            ? "inactive"
            : userType}{" "}
          user{filteredUsers.length !== 1 ? "s" : ""} found
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label={`Search ${title}`}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <IconButton
            onClick={handleClearSearch}
            size="small"
            title="Clear search"
            disabled={!searchTerm}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      </Box>

      {enableBulkDelete && filteredUsers.length > 0 && (
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box display="flex" alignItems="center">
            <Checkbox
              size="small"
              checked={
                selectedForDelete.length === filteredUsers.length
              }
              indeterminate={
                selectedForDelete.length > 0 &&
                selectedForDelete.length < filteredUsers.length
              }
              onChange={toggleSelectAllUsers}
            />
            <Typography variant="body2">Select All</Typography>
          </Box>
          {selectedForDelete.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleOpenBulkDeleteConfirm}
            >
              Delete Selected ({selectedForDelete.length})
            </Button>
          )}
        </Box>
      )}

      <Divider style={{ marginBottom: "20px" }} />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} style={{ marginBottom: "50px" }}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    {enableBulkDelete && (
                      <Grid item>
                        <Checkbox
                          checked={selectedForDelete.includes(user.id)}
                          onChange={() => toggleSelectUser(user.id)}
                        />
                      </Grid>
                    )}
                    <Grid item>
                      <Avatar
                        src={user.avatar}
                        alt={user.name}
                        sx={{ width: 80, height: 80 }}
                        onAvatarUploadSuccess={handleAvatarUploadSuccess}
                      />
                    </Grid>
                    <Grid item xs>
                      <Typography variant="h6" component="div">
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.phone}
                      </Typography>
                      {user.userTypes?.coordinator && (
                        <Typography variant="body2" color="text.secondary">
                          Partner: {user.tp_partner || "- None"}
                        </Typography>
                      )}

                      {/* Display user type chips */}
                      <Box mt={1} mb={1}>
                        {getUserTypeLabels(user).map((type, index) => (
                          <Chip
                            key={index}
                            label={type}
                            size="small"
                            color={
                              type === "Admin"
                                ? "primary"
                                : type === "Inactive"
                                ? "default"
                                : "secondary"
                            }
                            sx={{
                              mr: 0.5,
                              mb: 0.5,
                              backgroundColor:
                                type === "Inactive" ? "#f5f5f5" : undefined,
                              color:
                                type === "Inactive" ? "#757575" : undefined,
                            }}
                          />
                        ))}
                      </Box>

                      <Box display="flex" alignItems="center" gap={2}>
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          style={{ color: "purple", cursor: "pointer" }}
                          size="2x"
                          onClick={() => handleEditClick(user)}
                        />
                        <FontAwesomeIcon
                          icon={faLock}
                          title="Send password reset email"
                          style={{
                            color:
                              resettingPasswordFor === user.id
                                ? "#bbb"
                                : "#2b7cff",
                            cursor:
                              resettingPasswordFor === user.id
                                ? "default"
                                : "pointer",
                          }}
                          size="2x"
                          onClick={() =>
                            resettingPasswordFor === user.id
                              ? null
                              : handlePasswordReset(user)
                          }
                        />
                        <EditAvatar
                          user={user}
                          onAvatarUploadSuccess={handleAvatarUploadSuccess}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {filteredUsers.length === 0 && (
            <Grid item xs={12}>
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary">
                  No{" "}
                  {isTabbedType && tabValue === 1 ? "inactive " : ""}
                  {userType === "admin"
                    ? "admin"
                    : userType === "inactive"
                    ? "inactive"
                    : userType}{" "}
                  users found
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {selectedUser && (
        <EditTpUser
          open={editModalOpen}
          onClose={handleEditModalClose}
          user={selectedUser}
          onSuccess={handleEditSuccess}
          onUserDeleted={() => {
            fetchUsersByType();
            setSnackbarMessage("User has been deleted");
            setSnackbarOpen(true);
            setEditModalOpen(false);
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%", backgroundColor: "black", color: "white" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {enableBulkDelete && (
        <ConfirmDeleteDialog
          open={bulkDeleteConfirmOpen}
          onClose={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={handleBulkDeleteConfirmed}
          deleting={bulkDeleting}
          title="Delete Selected Users"
          message={`Are you sure you want to delete ${selectedForDelete.length} selected user${
            selectedForDelete.length === 1 ? "" : "s"
          }? This permanently removes them from both the sign-in system and the users list. This action cannot be undone.`}
        />
      )}
    </Container>
  );
};

export default TpUsersByType;
